import Box from "@mui/material/Box";
import Badge from "@mui/material/Badge";
import { forwardRef, useEffect, useState } from "react";
import { RouterLink } from "src/routes/components";
import Slide from "@mui/material/Slide";
import Iconify from "src/components/iconify";
import { useBoolean } from "src/hooks/use-boolean";
import Dialog from "@mui/material/Dialog";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { t } from "i18next";
import CartItem from "./cart-item";
import { DialogContent, DialogTitle } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import Stack from "@mui/material/Stack";
import { _applications } from "src/_mock";
import {
  deleteCartTransaction,
  useGetActiveCartTransactions,
} from "src/api/transaction";
import { enqueueSnackbar } from "src/components/snackbar";
import { useTransaction } from "../checkout/context";
import { ITransaction } from "src/types/transaction";
import { getTimezoneAdjustedDate } from "src/utils/format-time";
// ----------------------------------------------------------------------

const Transition = forwardRef(
  (
    props: TransitionProps & {
      children: React.ReactElement;
    },
    ref: React.Ref<unknown>
  ) => <Slide direction="up" ref={ref} {...props} />
);

export default function CartIcon() {
  const dialog = useBoolean();

  const {
    activeTransaction,
    setActiveTransaction,
    needToRefreshCart,
    setNeedToRefreshCart,
  } = useTransaction();

  const {
    transactions: fetchedTransactions,
    transactionsLoading,
    transactionsError,
    mutate,
  } = useGetActiveCartTransactions();

  const [transactions, setTransactions] = useState([]);

  // Initialize transactions from fetched data
  useEffect(() => {
    if (fetchedTransactions) {
      setTransactions(fetchedTransactions);
    }
  }, [fetchedTransactions]);

  // Check deadlines and remove expired transactions every minute
  useEffect(() => {
    const ONE_MINUTE = 60 * 1000; // 1 minute in milliseconds
    const interval = setInterval(() => {
      const now = getTimezoneAdjustedDate(new Date(), "plus1");

      setTransactions((prevTransactions) =>
        prevTransactions.filter((transaction: ITransaction) => {
          const deadlineDate = getTimezoneAdjustedDate(
            transaction.deadline,
            "plus1"
          );

          // Remove expired transactions
          if (deadlineDate < now) {
            if (activeTransaction?.id === transaction.id) {
              setActiveTransaction({});
            }
            if (transaction.id) {
              deleteCartTransaction(transaction.id)
                .then(() => {
                  enqueueSnackbar(
                    "Transaction deleted because the deadline expired.",
                    {
                      variant: "info",
                    }
                  );

                  setNeedToRefreshCart(true); // Trigger a refresh if needed
                })
                .catch((error) => {
                  console.error(
                    "Error deleting transaction after deadline:",
                    error
                  );
                });

              enqueueSnackbar(`Transaction expired and was removed.`, {
                variant: "info",
              });
            }
            return false; // Exclude expired transaction
          }

          return true; // Keep valid transactions
        })
      );
    }, ONE_MINUTE);

    // Cleanup interval on unmount
    return () => clearInterval(interval);
  }, [activeTransaction, setActiveTransaction]);

  useEffect(() => {
    if (needToRefreshCart) {
      mutate()
        .then(() => {
          setNeedToRefreshCart(false); // Reset the refresh flag
        })
        .catch((err) => {
          console.error("Failed to refresh transactions:", err);
        });
    }
  }, [needToRefreshCart, setNeedToRefreshCart, mutate]);

  const totalTransactionItems = transactions?.length || 0;

  const showCart =
    !transactionsLoading && !transactionsError && totalTransactionItems;

  const handleOnDelete = async (transactionId: number) => {
    try {
      const res = await deleteCartTransaction(transactionId);
      if (res.status === 204) {
        if (activeTransaction.id === transactionId) {
          setActiveTransaction({});
          if (window.location.href.includes("checkout")) {
            window.location.reload();
          }
        }

        enqueueSnackbar(t("action_success"), {
          variant: "success",
        });
      } else {
        enqueueSnackbar("Failed to delete transaction.", {
          variant: "error",
        });
      }
      setNeedToRefreshCart(true);
    } catch (error) {
      setNeedToRefreshCart(true);
      console.error("Error deleting transaction:", error);
      if (
        error.detail &&
        error.detail ===
          "failed_to_delete_transaction: 404: transaction_not_found_or_cannot_be_deleted"
      ) {
        setActiveTransaction({});
        mutate()
          .then(() => {
            setNeedToRefreshCart(false); // Reset the refresh flag
          })
          .catch((err) => {
            console.error("Failed to refresh transactions:", err);
          });
      }
      enqueueSnackbar("An error occurred while deleting the transaction.", {
        variant: "error",
      });
    }
  };

  const variables: any[] = [transactions];

  const renderVariables = variables.map((variable, index) => {
    if (Array.isArray(variable)) {
      // Render array items as a list
      return (
        <div key={`variable-${index}`}>
          <Typography variant="subtitle2">Array:</Typography>
          {variable.map((item, i) => (
            <Typography key={`array-item-${index}-${i}`} variant="body2">
              {JSON.stringify(item, null, 2)}
            </Typography>
          ))}
        </div>
      );
    } else if (variable && typeof variable === "object") {
      // Render object properties
      return (
        <div key={`variable-${index}`}>
          <Typography variant="subtitle2">Object:</Typography>
          <pre>{JSON.stringify(variable, null, 2)}</pre>
        </div>
      );
    } else if (typeof variable === "number" || typeof variable === "string") {
      // Render numbers and strings directly
      return (
        <Typography key={`variable-${index}`} variant="subtitle2">
          {`Value: ${variable}`}
        </Typography>
      );
    } else {
      // Handle null, undefined, or unsupported types
      return (
        <Typography key={`variable-${index}`} variant="subtitle2" color="error">
          {`Unsupported or null value: ${variable}`}
        </Typography>
      );
    }
  });

  return (
    <>
      {!!showCart && (
        <Box
          component={RouterLink}
          onClick={dialog.onTrue}
          // href={paths.product.checkout}
          sx={{
            right: 0,
            top: 112,
            zIndex: 999,
            display: "flex",
            cursor: "pointer",
            position: "fixed",
            color: "text.primary",
            borderTopLeftRadius: 16,
            borderBottomLeftRadius: 16,
            bgcolor: "background.paper",
            padding: (theme) => theme.spacing(1, 3, 1, 2),
            boxShadow: (theme) => theme.customShadows.dropdown,
            transition: (theme) => theme.transitions.create(["opacity"]),
            "&:hover": { opacity: 0.72 },
          }}
        >
          <Badge
            showZero
            badgeContent={totalTransactionItems}
            color="error"
            max={99}
          >
            <Iconify icon="solar:cart-3-bold" width={24} />
          </Badge>
        </Box>
      )}
      <Dialog
        fullScreen
        PaperProps={{
          sx: { maxWidth: 1150, minHeight: 300, maxHeight: 800 },
        }}
        open={dialog.value}
        onClose={dialog.onFalse}
        TransitionComponent={Transition}
      >
        <DialogTitle>
          <Toolbar>
            <Typography variant="h6" sx={{ flex: 1, ml: 2 }}>
              {t("cart")}
            </Typography>

            <IconButton color="inherit" edge="start" onClick={dialog.onFalse}>
              <Iconify icon="mingcute:close-line" />
            </IconButton>
          </Toolbar>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {transactionsLoading ? (
              <Typography>Loading...</Typography>
            ) : transactionsError ? (
              <Typography>Error loading transactions</Typography>
            ) : transactions.length ? (
              transactions.map((transaction: any) => (
                <CartItem
                  key={transaction.id}
                  transaction={transaction}
                  onClose={dialog.onFalse}
                  onDelete={() => handleOnDelete(transaction.id)}
                />
              ))
            ) : (
              <Typography>{t("no_transactions_added")}</Typography>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
